import { useEffect, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box, Chip, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';

import { errorReporter } from '@/services/errorReportingService';
import { SecondBrainChat, SecondBrainContextNote } from '../types';
import { SourceNoteCard } from './SourceNoteCard';

interface AnswerCardProps {
  chat: SecondBrainChat;
  isStreaming: boolean;
  sources: SecondBrainContextNote[];
  isFetchingSources: boolean;
  onOpenNote: (noteId: string) => void;
}

const markdownStyles = {
  '& p': { margin: 0, marginBottom: '14px', fontSize: '0.9375rem', lineHeight: 1.55, color: 'text.primary' },
  '& p:last-child': { marginBottom: 0 },
  '& h1': { fontSize: '1.125rem', fontWeight: 600, marginBottom: 1, marginTop: 1.5 },
  '& h2': { fontSize: '1rem', fontWeight: 600, marginBottom: 1, marginTop: 1.5 },
  '& h3': { fontSize: '0.9375rem', fontWeight: 600, marginBottom: 0.75, marginTop: 1.25 },
  '& ul, & ol': { margin: 0, marginBottom: '16px', paddingLeft: '20px', fontSize: '0.9375rem' },
  '& li': { marginBottom: '8px', lineHeight: 1.55 },
  '& li:last-child': { marginBottom: 0 },
  '& strong': { fontWeight: 600 },
  '& blockquote': {
    margin: 0,
    marginBottom: '18px',
    padding: '12px 18px',
    borderLeft: '3px solid',
    borderColor: 'secondary.main',
    bgcolor: 'grey.50',
    borderRadius: '0 8px 8px 0',
    fontStyle: 'italic',
  },
  '& blockquote p': { marginBottom: '6px' },
  '& blockquote p:last-child': { marginBottom: 0 },
  '& code': { bgcolor: 'action.hover', padding: '2px 6px', borderRadius: '4px', fontSize: '0.85em' },
  '& pre': { bgcolor: 'action.hover', padding: 1.5, borderRadius: 1, overflow: 'auto', marginBottom: 1 },
  '& pre code': { bgcolor: 'transparent', padding: 0 },
  '& a': { color: 'primary.main', textDecoration: 'none' },
};

export const AnswerCard = ({ chat, isStreaming, sources, isFetchingSources, onOpenNote }: AnswerCardProps) => {
  const [copied, setCopied] = useState(false);
  const copyResetTimeoutRef = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(copyResetTimeoutRef.current), []);

  const answer = chat.answer ?? '';
  const isThinking = isStreaming && !answer;
  const showSources = sources.length > 0 || isFetchingSources;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(answer);
      setCopied(true);
      window.clearTimeout(copyResetTimeoutRef.current);
      copyResetTimeoutRef.current = window.setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      errorReporter.expected('Failed to copy Second Brain answer', error, {
        feature: 'secondBrain',
        operation: 'copyAnswer',
      });
    }
  };

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'neutral.otherBorder',
        borderRadius: '15px',
        p: { xs: '18px 16px 14px', sm: '24px 28px 20px' },
      }}
    >
      <Stack direction="row" alignItems="center" gap={1.25} flexWrap="wrap" sx={{ mb: 2 }}>
        <Box
          sx={{
            width: 26,
            height: 26,
            borderRadius: '50%',
            bgcolor: 'rgba(43, 159, 255, 0.12)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            flex: 'none',
          }}
        >
          <PsychologyOutlinedIcon sx={{ fontSize: 16, color: 'primary.main' }} />
        </Box>
        <Typography sx={{ fontWeight: 600, fontSize: 13, color: 'text.primary' }}>Second Brain</Typography>
        {chat.tags.length > 0 && (
          <>
            <Box sx={{ width: '1px', height: 14, bgcolor: 'neutral.otherBorder' }} />
            <Typography variant="caption" color="text.secondary">
              searched
            </Typography>
            {chat.tags.map((tag) => (
              <Chip
                key={tag}
                label={tag}
                size="small"
                sx={{ height: 22, fontSize: 12, borderRadius: '4px', bgcolor: 'grey.100', color: 'text.primary' }}
              />
            ))}
          </>
        )}
      </Stack>

      {isThinking ? (
        <Stack direction="row" alignItems="center" gap={1.5} sx={{ py: 1 }}>
          <CircularProgress size={18} />
          <Typography variant="body2" color="text.secondary">
            Thinking…
          </Typography>
        </Stack>
      ) : answer ? (
        <Box sx={markdownStyles}>
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{answer}</ReactMarkdown>
        </Box>
      ) : chat.error ? (
        <Stack direction="row" alignItems="center" gap={1} sx={{ py: 1 }}>
          <WarningAmberOutlinedIcon sx={{ fontSize: 18, color: 'error.main' }} />
          <Typography variant="body2" color="error.main">
            {chat.error}
          </Typography>
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          No response yet
        </Typography>
      )}

      {showSources && !isThinking && (
        <>
          <Stack direction="row" alignItems="center" gap={1} sx={{ mt: 2.25, mb: 1.5 }}>
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: 11,
                lineHeight: '16px',
                letterSpacing: '0.6px',
                textTransform: 'uppercase',
                color: 'text.secondary',
              }}
            >
              Notes this came from
            </Typography>
            <Box sx={{ flex: 1, height: '1px', bgcolor: 'neutral.otherBorder' }} />
            <Typography variant="caption" color="text.secondary">
              {sources.length} note{sources.length === 1 ? '' : 's'}
            </Typography>
          </Stack>
          {isFetchingSources && sources.length === 0 ? (
            <Stack direction="row" alignItems="center" gap={1.5} sx={{ py: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="caption" color="text.secondary">
                Finding the notes…
              </Typography>
            </Stack>
          ) : (
            <Stack spacing={1}>
              {sources.map((note) => (
                <SourceNoteCard key={note.id} note={note} onOpen={onOpenNote} />
              ))}
            </Stack>
          )}
        </>
      )}

      {answer && !isStreaming && (
        <Stack
          direction="row"
          alignItems="center"
          gap={0.5}
          sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'neutral.otherBorder' }}
        >
          <Tooltip title={copied ? 'Copied' : 'Copy answer'}>
            <IconButton size="small" onClick={() => void handleCopy()} aria-label="Copy answer">
              {copied ? (
                <CheckIcon sx={{ fontSize: 18, color: 'success.main' }} />
              ) : (
                <ContentCopyIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Tooltip>
          {sources.length > 0 && (
            <Tooltip title="Open first source note">
              <IconButton size="small" onClick={() => onOpenNote(sources[0].id)} aria-label="Open first source note">
                <NorthEastIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
          <Box sx={{ flex: 1 }} />
          <Typography variant="caption" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
            Answered from your notes · nothing outside NoteGo
          </Typography>
        </Stack>
      )}
    </Box>
  );
};

export default AnswerCard;
